/**
 * Het schermvullende bericht dat een chatter pas kan wegklikken na lezen, en
 * het rode blokkeerscherm (28-10-2026, Jordy).
 *
 * De verzendkant zit in de Shift agenda ("Message or block a chatter"). De
 * ontvangstkant ontbrak in v2: Layout las /chatter-notices/mine als lijst,
 * terwijl de server `{account_blocked, notices}` stuurt. Een verstuurd
 * bericht kwam daardoor nooit in beeld en een geblokkeerde chatter zag niet
 * waarom alles weigerde.
 *
 * - Een bericht gaat alleen weg met "I have read this" (POST /mine/{id}/ack);
 *   dat is ook de leesbevestiging die de manager in de geschiedenis ziet.
 * - Is het account geblokkeerd, dan blijft het rode scherm staan. De server
 *   weigert dan elk ander verzoek; alleen uitloggen kan nog.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { api, uitloggen } from '../lib/api'
import { useApi } from '../lib/useApi'

export interface MijnBerichten {
  account_blocked: boolean
  blocked_by?: string | null
  blocked_reason?: string | null
  notices: { id: number; message: string; created_at: string | null
             created_by: string | null; is_block?: boolean }[]
}

export function useMijnBerichten() {
  return useApi<MijnBerichten>('/api/admin/chatter-notices/mine')
}

/** Een tijd zonder offset is UTC (de server schrijft naïef UTC). */
function alsUtc(t: string): Date {
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(t) ? t : `${t}Z`)
}

/** De data komt uit Layout (één poll voor de bel en dit scherm). */
export function VerplichtBericht({ mijn }: { mijn: ReturnType<typeof useMijnBerichten> }) {
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const data = mijn.data
  const eerste = data?.notices?.[0]
  if (!data || (!data.account_blocked && !eerste)) return null

  const geblokkeerd = Boolean(data.account_blocked)
  async function gelezen() {
    if (!eerste || eerste.id < 0) return
    setBezig(true)
    setFout(null)
    try {
      await api.post(`/api/admin/chatter-notices/mine/${eerste.id}/ack`, {})
      mijn.refetch()
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Could not confirm. Try again.')
    } finally {
      setBezig(false)
    }
  }

  // In <body> en boven alles: de intro-rondleiding (driver.js) zet tijdens een
  // eerste login pointer-events uit op de hele pagina; dit bericht moet dan
  // toch aan te klikken zijn.
  return createPortal(
    <div className={geblokkeerd ? 'vb-scherm vb-blok' : 'vb-scherm'} role="alertdialog"
         aria-modal="true" data-testid={geblokkeerd ? 'blokkeerscherm' : 'verplicht-bericht'}>
      <div className="vb-kaart">
        <h2>{geblokkeerd ? 'Your account is blocked' : 'Message from management'}</h2>
        {eerste?.created_by ? (
          <div className="vb-van">
            From {eerste.created_by}
            {eerste.created_at ? ` · ${alsUtc(eerste.created_at).toLocaleString('en-GB')}` : ''}
          </div>
        ) : null}
        <div className="vb-tekst">{eerste?.message
          ?? `Your account has been blocked${data.blocked_by ? ` by ${data.blocked_by}` : ''}.`}</div>
        {fout ? <div className="vb-fout">{fout}</div> : null}
        <div className="vb-knoppen">
          {geblokkeerd ? (
            <button className="btn sm" type="button" onClick={() => { void uitloggen() }}>
              Sign out
            </button>
          ) : (
            <button className="btn primary" type="button" disabled={bezig}
                    data-testid="bericht-gelezen" onClick={() => { void gelezen() }}>
              {bezig ? 'Confirming…' : 'I have read this'}
            </button>
          )}
        </div>
        {!geblokkeerd && (data.notices?.length ?? 0) > 1 ? (
          <div className="vb-van">{data.notices.length - 1} more after this one</div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
